/**
 * B930 — where the keyboard lands when a view changes.
 *
 * A single-page walk swaps whole views under the buyer: pressing BOOK
 * unmounts the list (and the pressed button with it), CONTINUE TO CART
 * unmounts the tickets step, KEEP SHOPPING unmounts the cart. The browser's
 * answer to "the focused element left the document" is `<body>` — so the
 * next Tab restarts at the top of the page and a screen reader announces
 * nothing at all. Measured on dev 2026-10-20 (e2e-ferry, every press).
 *
 * The cure is one rule, applied only when it cannot hurt: when the VIEW KEY
 * changes and the focus has nowhere to be (it sits on `<body>`, or on a node
 * that left the document), move it to the new view's heading — the element
 * marked `data-view-focus`, else the first visible `h1`/`h2` under the root.
 * A control that still holds the focus (a stepper's ±, a field being typed
 * in) keeps it: re-renders inside one view never move the keyboard, and the
 * first mount never steals it from the page.
 *
 * B934 — nor does a LOAD. A view key can change with nobody pressing anything
 * (the storefront's busy face: a first load passes through "Finding times…"),
 * and a fresh document's focus sits on `<body>`, which reads as lost: "never
 * had it" and "lost it" look the same. On dev that pulled a host page's focus
 * into the embedded widget's iframe and scrolled the host to it, and ringed a
 * deep-linked product's heading on arrival. So the hook moves the keyboard
 * only once the buyer has ENGAGED this document since mount — a pointerdown,
 * keydown or focusin inside it. A swap the buyer caused always follows one.
 */
import { useEffect, useRef, type RefObject } from "react";

/** B934 — the events that say the buyer is working THIS document (a load fires none of them). */
const ENGAGEMENT = ["pointerdown", "keydown", "focusin"] as const;

/** The attribute a view puts on the element its focus should land on. */
export const VIEW_FOCUS_ATTR = "data-view-focus";

const shown = (el: HTMLElement): boolean => {
  if (el.closest("[hidden], [aria-hidden='true'], [inert]") !== null) return false;
  const check = (el as HTMLElement & { checkVisibility?: () => boolean }).checkVisibility;
  return typeof check === "function" ? check.call(el) : true;
};

/** True when the document's focus has nowhere to be — the moment a swapped view may take it. */
export function focusIsLost(doc: Document): boolean {
  const active = doc.activeElement;
  return active === null || active === doc.body || active === doc.documentElement || !active.isConnected;
}

/**
 * Move a lost focus to the view's heading under `root`. Returns the element
 * focused, or undefined when the focus was not lost or no heading is shown.
 */
export function focusViewHeading(root: ParentNode | null | undefined): HTMLElement | undefined {
  if (root === null || root === undefined) return undefined;
  const doc = (root as Node).ownerDocument ?? (root as Document);
  if (!focusIsLost(doc)) return undefined;
  const pick = (selector: string): HTMLElement | undefined =>
    Array.from(root.querySelectorAll<HTMLElement>(selector)).find(shown);
  const target = pick(`[${VIEW_FOCUS_ATTR}]`) ?? pick("h1, h2");
  if (target === undefined) return undefined;
  if (!target.hasAttribute("tabindex")) target.setAttribute("tabindex", "-1");
  target.focus();
  return target;
}

/**
 * Attach the returned ref to the view's root (the page's `<main>`); pass a
 * key that names the view. On every key change after the first, a lost
 * focus lands on the new view's heading. B932: a page whose views each draw
 * their own `<main>` may leave the ref unattached — the hook then reads the
 * document, so the swap still lands somewhere.
 */
export function useViewFocus<T extends HTMLElement = HTMLElement>(viewKey: string): RefObject<T> {
  const ref = useRef<T>(null);
  const first = useRef(true);
  const engaged = useRef(false);
  useEffect(() => {
    const doc = ref.current?.ownerDocument ?? (typeof document !== "undefined" ? document : undefined);
    if (doc === undefined) return undefined;
    const engage = (): void => {
      engaged.current = true;
    };
    for (const type of ENGAGEMENT) doc.addEventListener(type, engage, true);
    return () => {
      for (const type of ENGAGEMENT) doc.removeEventListener(type, engage, true);
    };
  }, []);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (!engaged.current) return;
    focusViewHeading(ref.current ?? (typeof document !== "undefined" ? document : undefined));
  }, [viewKey]);
  return ref;
}

/** The tab's title follows the view (WCAG 2.4.2); an empty title leaves the document's own. */
export function useDocumentTitle(title: string | undefined): void {
  useEffect(() => {
    if (title !== undefined && title.trim() !== "") document.title = title;
  }, [title]);
}
